import { generateClient } from "aws-amplify/api";
import { type GraphQLResult } from "@aws-amplify/api";

import { partialGraphqlData } from "./graphqlPartial";

const client = generateClient();

// A ceiling on every paginated list, so a runaway page count can't hang a screen
export const DEFAULT_MAX_PAGES = 20;

interface ListAllPagesOptions {
  /** Names the list in console warnings, e.g. "asset groups" */
  label: string;
  maxPages?: number;
  /** Keep the readable part of a page whose nested records are partly unauthorized, instead of failing the list */
  allowPartial?: boolean;
  /** Return the rows read so far (with `error` set) when a page fails, instead of throwing */
  keepOnError?: boolean;
}

export interface ListAllPagesResult<T> {
  items: T[];
  /** False when the page ceiling stopped the read with rows still left */
  complete: boolean;
  /** Set only with keepOnError: the failure that cut the list short */
  error?: unknown;
}

type Connection<T> = { items?: (T | null)[] | null; nextToken?: string | null } | null;

/**
 * Reads a list query to its last page. AppSync returns at most `limit` rows a call (100 when unset), and owner and
 * org filters run AFTER that read, so one page can hold far fewer rows than the caller can see. Errors are thrown;
 * null items (unreadable rows in a partial result) are dropped.
 */
export async function listAllPages<T>(
  query: string,
  root: string,
  variables: Record<string, unknown>,
  { label, maxPages = DEFAULT_MAX_PAGES, allowPartial = false, keepOnError = false }: ListAllPagesOptions
): Promise<ListAllPagesResult<T>> {
  const items: T[] = [];
  let nextToken: string | null | undefined = null;
  let page = 0;

  do {
    let data: Record<string, Connection<T>> | undefined;
    try {
      const result = (await client.graphql({
        query,
        variables: { ...variables, nextToken },
      })) as GraphQLResult<Record<string, Connection<T>>>;
      data = result.data;
    } catch (error) {
      try {
        if (!allowPartial) throw error;
        data = partialGraphqlData<Record<string, Connection<T>>>(error, root, label);
      } catch (fatal) {
        if (keepOnError) return { items, complete: false, error: fatal };
        throw fatal;
      }
    }
    const connection = data?.[root];
    items.push(...((connection?.items ?? []).filter(Boolean) as T[]));
    nextToken = connection?.nextToken;
    page += 1;
  } while (nextToken && page < maxPages);

  if (nextToken) console.warn(`${label}: stopped after ${maxPages} pages, the list is incomplete`);
  return { items, complete: !nextToken };
}
